import {
    Questionnaire,
    QuestionnaireItem,
    QuestionnaireResponse,
    QuestionnaireResponseItem,
    QuestionnaireResponseItemAnswer,
} from '../types/fhir';

export interface QuestionnaireResponseCsvTaskInfo {
    id: string;
    patientName?: string;
    patientId?: string;
    questionnaireName?: string;
    questionnaireId?: string;
    status?: string;
    updatedAt?: string;
}

export interface QuestionnaireResponseCsvInput {
    task: QuestionnaireResponseCsvTaskInfo;
    questionnaireResponse: QuestionnaireResponse;
    questionnaire?: Questionnaire;
    exportedAt?: Date;
}

export interface QuestionnaireResponseCsvResult {
    filename: string;
    csv: string;
    questionRowCount: number;
}

type CsvScalar = string | number | boolean | null | undefined;

type FlattenedQuestionnaireItem = {
    linkId: string;
    text?: string;
    type?: string;
    definition?: string;
    required?: boolean;
    repeats?: boolean;
    readOnly?: boolean;
    answerValueSet?: string;
    prefix?: string;
    path: string;
    level: number;
    parentLinkId?: string;
    occurrenceIndex: number;
    item: QuestionnaireItem;
};

type FlattenedResponseItem = {
    linkId: string;
    text?: string;
    definition?: string;
    path: string;
    level: number;
    parentLinkId?: string;
    occurrenceIndex: number;
    item: QuestionnaireResponseItem;
    answers: QuestionnaireResponseItemAnswer[];
};

type SerializedAnswer = {
    type: string;
    display: string;
    code: string;
    system: string;
    unit: string;
    rawValue: string;
};

const METADATA_HEADERS = [
    'patient_name',
    'patient_user_id',
    'task_id',
    'task_status',
    'task_updated_at_utc',
    'questionnaire_name',
    'questionnaire_id',
    'response_id',
    'response_status',
    'response_authored_utc',
    'response_last_updated_utc',
    'subject_reference',
    'subject_display',
    'author_reference',
    'author_display',
    'exported_at_utc',
    'questionnaire_loaded',
] as const;

const QUESTION_HEADERS = [
    'row_index',
    'item_link_id',
    'item_text',
    'item_type',
    'item_definition',
    'item_required',
    'item_repeats',
    'item_read_only',
    'item_answer_value_set',
    'item_prefix',
    'item_path',
    'item_level',
    'item_parent_link_id',
    'item_occurrence_index',
    'answer_index',
    'answer_type',
    'answer_display',
    'answer_code',
    'answer_system',
    'answer_unit',
    'answer_value_raw',
    'answer_json',
    'questionnaire_item_json',
    'response_item_json',
    'is_answered',
    'is_in_questionnaire',
] as const;

export function buildQuestionnaireResponseCsv(input: QuestionnaireResponseCsvInput): QuestionnaireResponseCsvResult {
    const { task, questionnaireResponse, questionnaire } = input;
    const exportedAt = input.exportedAt ?? new Date();

    const subjectReference = questionnaireResponse.subject?.reference || '';
    const subjectDisplay = questionnaireResponse.subject?.display || '';
    const authorReference = questionnaireResponse.author?.reference || '';
    const authorDisplay = questionnaireResponse.author?.display || '';

    const responseAuthoredUtc = toUtcIsoString(questionnaireResponse.authored);
    const responseLastUpdatedUtc = toUtcIsoString(questionnaireResponse.meta?.lastUpdated);
    const taskUpdatedAtUtc = toUtcIsoString(task.updatedAt);
    const exportedAtUtc = exportedAt.toISOString();

    const patientNameRaw =
        firstNonEmpty(task.patientName, subjectDisplay, authorDisplay) ||
        extractIdFromReference(subjectReference) ||
        'unknown-patient';
    const patientUserIdRaw = firstNonEmpty(task.patientId, extractIdFromReference(subjectReference)) || 'unknown-user';
    const answeredDateTimeRaw =
        firstNonEmpty(questionnaireResponse.authored, questionnaireResponse.meta?.lastUpdated) || 'unknown-datetime';

    const questionnaireId =
        firstNonEmpty(
            task.questionnaireId,
            extractIdFromReference(questionnaireResponse.questionnaire),
            questionnaire?.id,
        ) || '';
    const questionnaireName =
        firstNonEmpty(questionnaire?.name, questionnaire?.title, task.questionnaireName, questionnaireId) || '';

    const filename = [
        sanitizeFileNameSegment(patientNameRaw, 'unknown-patient'),
        sanitizeFileNameSegment(patientUserIdRaw, 'unknown-user'),
        sanitizeFileNameSegment(formatUtcForFileName(answeredDateTimeRaw), 'unknown-datetime'),
    ].join('_') + '.csv';

    const metadataValues: CsvScalar[] = [
        patientNameRaw,
        patientUserIdRaw,
        task.id,
        task.status || '',
        taskUpdatedAtUtc,
        questionnaireName,
        questionnaireId,
        questionnaireResponse.id || '',
        questionnaireResponse.status || '',
        responseAuthoredUtc,
        responseLastUpdatedUtc,
        subjectReference,
        subjectDisplay,
        authorReference,
        authorDisplay,
        exportedAtUtc,
        questionnaire ? 'true' : 'false',
    ];

    const questionnaireItems = flattenQuestionnaireItems(questionnaire?.item);
    const responseItems = flattenResponseItems(questionnaireResponse.item);
    const questionRows = buildQuestionRows(questionnaireItems, responseItems);

    const csvRows: CsvScalar[][] = [
        [...METADATA_HEADERS],
        metadataValues,
        [],
        [...QUESTION_HEADERS],
        ...questionRows,
    ];

    const csv = '\uFEFF' + csvRows.map(toCsvRow).join('\r\n');

    return {
        filename,
        csv,
        questionRowCount: questionRows.length,
    };
}

function buildQuestionRows(
    questionnaireItems: FlattenedQuestionnaireItem[],
    responseItems: FlattenedResponseItem[],
): CsvScalar[][] {
    const rows: CsvScalar[][] = [];
    let rowIndex = 1;

    const responseByLinkId = groupByLinkId(responseItems);
    const questionnaireLinkIds = new Set(questionnaireItems.map((item) => item.linkId));

    if (questionnaireItems.length > 0) {
        questionnaireItems.forEach((questionnaireItem) => {
            const matchingResponseItems = responseByLinkId.get(questionnaireItem.linkId) || [];

            if (matchingResponseItems.length === 0) {
                rows.push(
                    createQuestionRow({
                        rowIndex,
                        questionnaireItem,
                        isInQuestionnaire: true,
                    }),
                );
                rowIndex += 1;
                return;
            }

            matchingResponseItems.forEach((responseItem) => {
                if (responseItem.answers.length === 0) {
                    rows.push(
                        createQuestionRow({
                            rowIndex,
                            questionnaireItem,
                            responseItem,
                            isInQuestionnaire: true,
                        }),
                    );
                    rowIndex += 1;
                    return;
                }

                responseItem.answers.forEach((answer, answerIdx) => {
                    rows.push(
                        createQuestionRow({
                            rowIndex,
                            questionnaireItem,
                            responseItem,
                            answer,
                            answerIndex: answerIdx + 1,
                            isInQuestionnaire: true,
                        }),
                    );
                    rowIndex += 1;
                });
            });
        });

        responseItems.forEach((responseItem) => {
            if (questionnaireLinkIds.has(responseItem.linkId)) {
                return;
            }

            if (responseItem.answers.length === 0) {
                rows.push(
                    createQuestionRow({
                        rowIndex,
                        responseItem,
                        isInQuestionnaire: false,
                    }),
                );
                rowIndex += 1;
                return;
            }

            responseItem.answers.forEach((answer, answerIdx) => {
                rows.push(
                    createQuestionRow({
                        rowIndex,
                        responseItem,
                        answer,
                        answerIndex: answerIdx + 1,
                        isInQuestionnaire: false,
                    }),
                );
                rowIndex += 1;
            });
        });

        return rows;
    }

    responseItems.forEach((responseItem) => {
        if (responseItem.answers.length === 0) {
            rows.push(
                createQuestionRow({
                    rowIndex,
                    responseItem,
                    isInQuestionnaire: false,
                }),
            );
            rowIndex += 1;
            return;
        }

        responseItem.answers.forEach((answer, answerIdx) => {
            rows.push(
                createQuestionRow({
                    rowIndex,
                    responseItem,
                    answer,
                    answerIndex: answerIdx + 1,
                    isInQuestionnaire: false,
                }),
            );
            rowIndex += 1;
        });
    });

    return rows;
}

function createQuestionRow({
    rowIndex,
    questionnaireItem,
    responseItem,
    answer,
    answerIndex,
    isInQuestionnaire,
}: {
    rowIndex: number;
    questionnaireItem?: FlattenedQuestionnaireItem;
    responseItem?: FlattenedResponseItem;
    answer?: QuestionnaireResponseItemAnswer;
    answerIndex?: number;
    isInQuestionnaire: boolean;
}): CsvScalar[] {
    const serializedAnswer = serializeAnswer(answer);
    const itemPath = responseItem?.path || questionnaireItem?.path || '';
    const itemLevel = responseItem?.level ?? questionnaireItem?.level ?? 0;
    const itemParentLinkId = responseItem?.parentLinkId || questionnaireItem?.parentLinkId || '';
    const itemOccurrenceIndex = responseItem?.occurrenceIndex ?? questionnaireItem?.occurrenceIndex ?? 0;
    const itemLinkId = questionnaireItem?.linkId || responseItem?.linkId || '';
    const itemText = questionnaireItem?.text || responseItem?.text || '';
    const itemDefinition = questionnaireItem?.definition || responseItem?.definition || '';
    const isAnswered = Boolean(answer);

    return [
        rowIndex,
        itemLinkId,
        itemText,
        questionnaireItem?.type || '',
        itemDefinition,
        booleanToString(questionnaireItem?.required),
        booleanToString(questionnaireItem?.repeats),
        booleanToString(questionnaireItem?.readOnly),
        questionnaireItem?.answerValueSet || '',
        questionnaireItem?.prefix || '',
        itemPath,
        itemLevel,
        itemParentLinkId,
        itemOccurrenceIndex,
        answerIndex || '',
        serializedAnswer.type,
        serializedAnswer.display,
        serializedAnswer.code,
        serializedAnswer.system,
        serializedAnswer.unit,
        serializedAnswer.rawValue,
        answer ? toCompactJson(answer) : '',
        questionnaireItem ? toCompactJson(questionnaireItem.item) : '',
        responseItem ? toCompactJson(responseItem.item) : '',
        booleanToString(isAnswered),
        booleanToString(isInQuestionnaire),
    ];
}

function flattenQuestionnaireItems(items: QuestionnaireItem[] | undefined): FlattenedQuestionnaireItem[] {
    if (!items || items.length === 0) {
        return [];
    }

    const flattened: FlattenedQuestionnaireItem[] = [];

    const walk = (
        currentItems: QuestionnaireItem[],
        parentPath: string,
        level: number,
        parentLinkId?: string,
    ): void => {
        const occurrenceByLinkId = new Map<string, number>();

        currentItems.forEach((item) => {
            const occurrenceIndex = occurrenceByLinkId.get(item.linkId) || 0;
            occurrenceByLinkId.set(item.linkId, occurrenceIndex + 1);

            const pathSegment = `${item.linkId}[${occurrenceIndex}]`;
            const path = parentPath ? `${parentPath}/${pathSegment}` : pathSegment;

            flattened.push({
                linkId: item.linkId,
                text: item.text,
                type: item.type,
                definition: item.definition,
                required: item.required,
                repeats: item.repeats,
                readOnly: item.readOnly,
                answerValueSet: item.answerValueSet,
                prefix: item.prefix,
                path,
                level,
                parentLinkId,
                occurrenceIndex,
                item,
            });

            if (item.item && item.item.length > 0) {
                walk(item.item, path, level + 1, item.linkId);
            }
        });
    };

    walk(items, '', 0);
    return flattened;
}

function flattenResponseItems(items: QuestionnaireResponseItem[] | undefined): FlattenedResponseItem[] {
    if (!items || items.length === 0) {
        return [];
    }

    const flattened: FlattenedResponseItem[] = [];

    const walk = (
        currentItems: QuestionnaireResponseItem[],
        parentPath: string,
        level: number,
        parentLinkId?: string,
    ): void => {
        const occurrenceByLinkId = new Map<string, number>();

        currentItems.forEach((item) => {
            const occurrenceIndex = occurrenceByLinkId.get(item.linkId) || 0;
            occurrenceByLinkId.set(item.linkId, occurrenceIndex + 1);

            const pathSegment = `${item.linkId}[${occurrenceIndex}]`;
            const path = parentPath ? `${parentPath}/${pathSegment}` : pathSegment;

            flattened.push({
                linkId: item.linkId,
                text: item.text,
                definition: item.definition,
                path,
                level,
                parentLinkId,
                occurrenceIndex,
                item,
                answers: item.answer || [],
            });

            if (item.item && item.item.length > 0) {
                walk(item.item, path, level + 1, item.linkId);
            }

            if (item.answer && item.answer.length > 0) {
                item.answer.forEach((answer, answerIdx) => {
                    if (answer.item && answer.item.length > 0) {
                        walk(answer.item, `${path}/answer[${answerIdx}]`, level + 1, item.linkId);
                    }
                });
            }
        });
    };

    walk(items, '', 0);
    return flattened;
}

function serializeAnswer(answer?: QuestionnaireResponseItemAnswer): SerializedAnswer {
    if (!answer) {
        return {
            type: '',
            display: '',
            code: '',
            system: '',
            unit: '',
            rawValue: '',
        };
    }

    if (answer.valueBoolean !== undefined) {
        return {
            type: 'boolean',
            display: answer.valueBoolean ? 'true' : 'false',
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueBoolean ? 'true' : 'false',
        };
    }

    if (answer.valueDecimal !== undefined) {
        return {
            type: 'decimal',
            display: answer.valueDecimal.toString(),
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueDecimal.toString(),
        };
    }

    if (answer.valueInteger !== undefined) {
        return {
            type: 'integer',
            display: answer.valueInteger.toString(),
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueInteger.toString(),
        };
    }

    if (answer.valueDate) {
        return {
            type: 'date',
            display: answer.valueDate,
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueDate,
        };
    }

    if (answer.valueDateTime) {
        return {
            type: 'dateTime',
            display: answer.valueDateTime,
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueDateTime,
        };
    }

    if (answer.valueTime) {
        return {
            type: 'time',
            display: answer.valueTime,
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueTime,
        };
    }

    if (answer.valueString !== undefined) {
        return {
            type: 'string',
            display: answer.valueString,
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueString,
        };
    }

    if (answer.valueUri) {
        return {
            type: 'uri',
            display: answer.valueUri,
            code: '',
            system: '',
            unit: '',
            rawValue: answer.valueUri,
        };
    }

    if (answer.valueCoding) {
        return {
            type: 'coding',
            display: answer.valueCoding.display || answer.valueCoding.code || '',
            code: answer.valueCoding.code || '',
            system: answer.valueCoding.system || '',
            unit: '',
            rawValue: toCompactJson(answer.valueCoding),
        };
    }

    if (answer.valueReference) {
        return {
            type: 'reference',
            display: answer.valueReference.display || answer.valueReference.reference || '',
            code: '',
            system: '',
            unit: '',
            rawValue: toCompactJson(answer.valueReference),
        };
    }

    if (answer.valueQuantity) {
        const valuePart = answer.valueQuantity.value !== undefined ? answer.valueQuantity.value.toString() : '';
        const unitPart = answer.valueQuantity.unit || answer.valueQuantity.code || '';
        const comparatorPart = answer.valueQuantity.comparator || '';
        const display = [comparatorPart, valuePart, unitPart].filter((part) => part.length > 0).join(' ').trim();

        return {
            type: 'quantity',
            display,
            code: answer.valueQuantity.code || '',
            system: answer.valueQuantity.system || '',
            unit: answer.valueQuantity.unit || answer.valueQuantity.code || '',
            rawValue: toCompactJson(answer.valueQuantity),
        };
    }

    if (answer.valueAttachment) {
        return {
            type: 'attachment',
            display:
                answer.valueAttachment.title ||
                answer.valueAttachment.url ||
                answer.valueAttachment.contentType ||
                'attachment',
            code: '',
            system: '',
            unit: '',
            rawValue: toCompactJson(answer.valueAttachment),
        };
    }

    const answerKeys = Object.keys(answer) as Array<keyof QuestionnaireResponseItemAnswer>;
    const dynamicValueKey = answerKeys.find((key) => key.startsWith('value'));

    return {
        type: dynamicValueKey ? dynamicValueKey.toString() : 'unknown',
        display: toCompactJson(answer),
        code: '',
        system: '',
        unit: '',
        rawValue: toCompactJson(answer),
    };
}

function groupByLinkId<T extends { linkId: string }>(items: T[]): Map<string, T[]> {
    return items.reduce((map, item) => {
        const existing = map.get(item.linkId) || [];
        existing.push(item);
        map.set(item.linkId, existing);
        return map;
    }, new Map<string, T[]>());
}

function toCsvRow(values: CsvScalar[]): string {
    return values.map((value) => escapeCsvValue(value)).join(',');
}

function escapeCsvValue(value: CsvScalar): string {
    const normalized = value === null || value === undefined ? '' : String(value);
    const escaped = normalized.replace(/"/g, '""');
    if (/[",\r\n]/.test(escaped)) {
        return `"${escaped}"`;
    }
    return escaped;
}

function booleanToString(value?: boolean): string {
    if (value === undefined) {
        return '';
    }
    return value ? 'true' : 'false';
}

export function firstNonEmpty(...values: Array<string | undefined>): string | undefined {
    return values.find((value) => Boolean(value && value.trim().length > 0));
}

function toCompactJson(value: unknown): string {
    try {
        return JSON.stringify(value);
    } catch {
        return '';
    }
}

export function extractIdFromReference(reference?: string): string | undefined {
    if (!reference || reference.trim().length === 0) {
        return undefined;
    }
    const normalized = reference.trim();
    if (!normalized.includes('/')) {
        return normalized;
    }
    const parts = normalized.split('/');
    return parts[parts.length - 1] || undefined;
}

function toUtcIsoString(value?: string): string {
    if (!value) {
        return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toISOString();
}

export function formatUtcForFileName(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'unknown-datetime';
    }

    const isoUtc = date.toISOString().replace(/\.\d{3}Z$/, 'Z');
    return isoUtc.replace(/:/g, '-');
}

export function sanitizeFileNameSegment(value: string, fallback: string): string {
    const stripped = value
        .replace(/[<>:"/\\|?*\x00-\x1f]/g, ' ')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
        .replace(/^-|-$/g, '');

    return stripped.length > 0 ? stripped : fallback;
}
